import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { TReaderPlace } from '@/components/organism/DocumentReader/types';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import {
  closeDocumentViewer,
  selectDocumentViewerResumePlace,
  yieldDocumentViewer,
} from '@/store/documentViewer/documentViewerSlice';
import { selectIsSidebarOpen } from '@/store/sidebar/sidebarSlice';
import { DOCUMENT_VIEWER_ELEMENT_ID } from '@/utils/constants';
import {
  clampDocumentViewerWidth,
  resolveDocumentViewerLayout,
} from '@/utils/documentViewerLayout/documentViewerLayout';
import { measureDocumentViewerSpace } from '@/utils/documentViewerLayout/measureSpace';

import DocumentViewerBody from './DocumentViewerBody';
import { DocumentViewerRegion } from './Style';
import type { TDocumentViewerPanelProps } from './types';
import { useDocumentViewerFocus } from './useDocumentViewerFocus';
import { useDocumentViewerKeyboard } from './useDocumentViewerKeyboard';
import { useDocumentViewerLayout } from './useDocumentViewerLayout';

const texts = {
  label: 'chatPage.documentViewer.label',
  close: 'chatPage.documentViewer.close',
  fullscreen: 'chatPage.documentViewer.fullscreen',
};

const BODY_CLASS_NAME = 'padding-0 overflow-hidden height-100pct';

const PANEL_SELECTOR = '.Sidebar';
const CLOSE_BUTTON_SELECTOR = '[data-testid="sidebarCloseButton"]';
const FULLSCREEN_BUTTON_SELECTOR = '[data-testid="sidebarFullscreenToggle"]';

const DocumentViewerPanel = ({ source, pageRequestId, sourceElementId }: TDocumentViewerPanelProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const resumePlace = useAppSelector(selectDocumentViewerResumePlace);
  const readerPlaceRef = useRef<TReaderPlace | null>(resumePlace);
  const isHistoryOpen = useAppSelector(selectIsSidebarOpen);
  const layout = useDocumentViewerLayout();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [preferredWidth, setPreferredWidth] = useState<number | null>(null);

  const width = clampDocumentViewerWidth(preferredWidth ?? layout.openingWidth, layout);
  const isModal = isFullScreen;

  const restoreFocus = useDocumentViewerFocus({ containerRef, headingRef, sourceElementId });

  const handleClose = useCallback(() => {
    dispatch(closeDocumentViewer());
    restoreFocus();
  }, [dispatch, restoreFocus]);

  useDocumentViewerKeyboard({ containerRef, isModal, isFullScreen, onClose: handleClose });

  const handleFullScreenChange = useCallback((isNowFullScreen: boolean) => setIsFullScreen(isNowFullScreen), []);

  const handleResizeEnd = useCallback(() => {
    const panelWidth = containerRef.current?.querySelector(PANEL_SELECTOR)?.getBoundingClientRect().width;
    if (panelWidth) setPreferredWidth(Math.round(panelWidth));
  }, []);

  useEffect(() => {
    const space = measureDocumentViewerSpace();
    // INFO: Opening a Document may have just collapsed the history, which is still animating closed, so its width
    // is left out once the store says it is closed.
    setPreferredWidth(resolveDocumentViewerLayout(isHistoryOpen ? space : { ...space, historyWidth: 0 }).openingWidth);
    // INFO: No dependencies, since the opening width is measured once, after the first render has laid the page out.
  }, []);

  useEffect(() => {
    // INFO: The history took the room the Viewer needs, so the Viewer gives it up and keeps its place for when its
    // Source is activated again.
    if (isHistoryOpen && !layout.fits) dispatch(yieldDocumentViewer(readerPlaceRef.current));
  }, [isHistoryOpen, layout.fits, dispatch]);

  useEffect(() => {
    // INFO: Rio's header buttons are icon-only and ship without an accessible name.
    // Since the Sidebar gives no way to pass one they are named here instead of replacing both controls with custom built.
    containerRef.current
      ?.querySelector(CLOSE_BUTTON_SELECTOR)
      ?.setAttribute('aria-label', intl.formatMessage({ id: texts.close }));
    containerRef.current
      ?.querySelector(FULLSCREEN_BUTTON_SELECTOR)
      ?.setAttribute('aria-label', intl.formatMessage({ id: texts.fullscreen }));
  }, [intl]);

  const title = useMemo(
    () => (
      <h2 ref={headingRef} tabIndex={-1} className="text-size-16 text-medium text-truncate margin-0 outline-none">
        {source.title}
      </h2>
    ),
    [source.title],
  );

  return (
    <ApplicationLayout.Sidebar className="right">
      <DocumentViewerRegion
        ref={containerRef}
        id={DOCUMENT_VIEWER_ELEMENT_ID}
        role={isModal ? 'dialog' : 'complementary'}
        aria-modal={isModal || undefined}
        aria-label={intl.formatMessage({ id: texts.label }, { title: source.title })}
      >
        <Sidebar
          position={Sidebar.RIGHT}
          title={title}
          showHeaderBorder
          resizable
          width={width}
          minWidth={layout.minWidth}
          maxWidth={layout.maxWidth}
          onResizeEnd={handleResizeEnd}
          enableFullscreenToggle
          // INFO: rio keeps its own full screen state. Starting it from ours keeps the two in step, or Esc and the
          // focus trap would act on a full screen that is gone.
          openInFullscreen={isFullScreen}
          fullscreenToggleTooltip={intl.formatMessage({ id: texts.fullscreen })}
          onFullScreenChange={handleFullScreenChange}
          onClose={handleClose}
          bodyClassName={BODY_CLASS_NAME}
        >
          <DocumentViewerBody source={source} pageRequestId={pageRequestId} placeRef={readerPlaceRef} />
        </Sidebar>
      </DocumentViewerRegion>
    </ApplicationLayout.Sidebar>
  );
};

export default DocumentViewerPanel;
